// Microbe RTS — app shell / entry point.
//
// Wires the four coherent layers into a running game:
//   • sim     (sim/sim.ts)      — authoritative, deterministic; owns + mutates all state
//   • render  (render/render.ts) — pure Canvas draw + the Camera it owns
//   • input   (ui/input.ts)      — smart-tap + gestures over the canvas, driving sim + cam
//   • hud     (ui/hud.ts)        — top bar + thumb-arc dock (self-refreshing on a timer too)
//   • evolve  (ui/evolve.ts)     — slide-up mutation sheet
//
// The three UI mounts already coordinate with each other through window CustomEvents
// (microbe:evolve-open / evolve-quickbuy / recenter / rally / nodeselect), so the shell only
// has to MOUNT them + run the loop. It also owns: the fixed-step loop, full-freeze on
// backgrounding, draining sim.state.events into toasts + haptics + audio blips, and the
// win/lose overlay.

import { createLoop, haptic, createAudio } from '../src/index.js';
import { createSim } from './sim/sim.js';
import { mountRender } from './render/render.js';
import { attachGameInput } from './ui/input.js';
import { attachMinimapInput } from './ui/minimap-input.js';
import { createFloatingJoystick } from './ui/floating-joystick.js';
import { mountHud } from './ui/hud.js';
import { mountEvolve } from './ui/evolve.js';
import { mountTutorial } from './ui/tutorial.js';
import { startReplay, type ReplayRecording } from './ui/replay.js';
import { maybeShowA2HS } from './ui/a2hs.js';
import { SEED_COST } from './config.js';
import type { Difficulty } from './config.js';
import type { GameEvent, Side, Command } from './types.js';
// PvP (BETA): single-engine input-lockstep netcode. Public API only — the transport/session own
// the WebSocket, the fixed input-delay, canonical command ordering, and the checksum canary.
import { detectEngine, connectMatch, createPvpSession } from './net/pvp.js';

// Structural handles derived from the net module's public API, so this wiring stays decoupled from
// its internals but still type-checks against the shared contract.
type Transport = ReturnType<typeof connectMatch>;
type PvpSession = ReturnType<typeof createPvpSession>;
// The running match is either solo (bot rival) or PvP (a live peer); this rides through runMatch.
interface PvpCtx {
  session: PvpSession;
  side: Side;
}

// ---- DOM: guard the load-bearing elements; fail loud if the shell is malformed ----
const canvas = document.getElementById('stage') as HTMLCanvasElement | null;
const hudRoot = document.getElementById('hud');
const evolveRoot = document.getElementById('evolve');
const toastRoot = document.getElementById('toast');

if (!canvas || !hudRoot || !evolveRoot || !toastRoot) {
  throw new Error(
    'Microbe RTS: missing #stage / #hud / #evolve / #toast. Cannot boot the shell.',
  );
}

// ---- cache guard ---------------------------------------------------------
// microbe-rts ships NO service worker of its own and serves with no-cache HTML, so a normal
// reload always lands the fresh build. But a sibling PWA (walkit) or the OLD /microbe/ game
// registers a SW on this same origin; if one ever ends up shadowing THIS page it would pin the
// app to a stale build. So on boot: unregister any SW whose scope covers this page, nuke the
// asset caches, and hard-reload exactly once. No-op in the normal case (nothing shadows us).
// (No visible build badge — it was a wrapper relic sitting in the bottom safe-area strip.)
(function cacheGuard(): void {
  if (!('serviceWorker' in navigator)) return;
  const RELOADED = 'mrts-cache-purged';
  navigator.serviceWorker
    .getRegistrations()
    .then(async (regs) => {
      let killed = false;
      for (const r of regs) {
        // only touch a registration whose scope could shadow this page
        if (location.href.startsWith(r.scope)) {
          await r.unregister();
          killed = true;
        }
      }
      if (!killed) return; // nothing shadows us -> leave sibling apps' caches alone
      if ('caches' in window) {
        try {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        } catch {
          /* best-effort */
        }
      }
      // Reload AT MOST ONCE EVER per device, gated on localStorage (persists across the reload,
      // unlike sessionStorage which iOS can drop — a dropped flag made this loop, flashing any
      // boot UI). Fail-SAFE: if storage can't be read/written, do NOT reload, so a storage error
      // can never spin an infinite reload loop.
      let purged = true;
      try { purged = localStorage.getItem(RELOADED) === '1'; } catch { purged = true; }
      if (purged) return;
      try { localStorage.setItem(RELOADED, '1'); } catch { return; }
      location.reload();
    })
    .catch(() => {
      /* SW API hiccup -> nothing to purge */
    });
})();

// ---- audio: a few procedural blips; auto-unlocks on the first gesture, never blocks boot ----
const audio = createAudio({
  sounds: {
    produce: [{ freq: 440, to: 620, dur: 0.06, type: 'triangle', vol: 0.09 }],
    attack: [{ freq: 220, to: 120, dur: 0.1, type: 'sawtooth', vol: 0.1 }],
    claim: [
      { freq: 520, dur: 0.05, type: 'sine', vol: 0.09 },
      { freq: 780, start: 0.05, dur: 0.09, type: 'sine', vol: 0.09 },
    ],
    evolve: [
      { freq: 523, dur: 0.07, type: 'square', vol: 0.1 },
      { freq: 659, start: 0.07, dur: 0.07, type: 'square', vol: 0.1 },
      { freq: 784, start: 0.14, dur: 0.12, type: 'square', vol: 0.1 },
    ],
    lost: [{ freq: 300, to: 90, dur: 0.22, type: 'sawtooth', vol: 0.12 }],
  },
});

// ---- toast: drain sim.state.events → a brief on-screen notice + audio + haptic ----
let toastTimer = 0;
function showToast(msg: string, ms = 2600): void {
  if (!toastRoot) return;
  toastRoot.textContent = msg;
  toastRoot.hidden = false;
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    if (toastRoot) toastRoot.hidden = true;
  }, ms);
}

const EVENT_TEXT: Record<GameEvent['kind'], (e: GameEvent) => string | null> = {
  underAttack: () => '⚠ Under attack!',
  nodeLost: () => '✖ Node lost',
  nodeClaimed: (e) => (e.side === 'you' ? '✔ Node claimed' : null),
  popCapped: (e) => (e.side === 'you' ? '⊘ Population capped' : null),
  rivalExpanded: () => null, // quiet — the minimap shows it
  oxyIgnite: () => '☁ The Great Oxygenation has begun',
  evolved: (e) => (e.side === 'you' ? `Evolved: ${e.text ?? ''}`.trim() : null),
  produced: () => null, // too frequent to toast; still blips below
  seedBlocked: (e) =>
    e.side === 'you' ? `⚑ Need ${SEED_COST.energy}⚡ ${SEED_COST.amino}🧬 to seed a new colony` : null,
};

// ---- match stats: accumulated over a game, rendered on the post-game screen ----
interface MatchStats {
  producedYou: number; // your cells bred (produced events, player-side)
  evolvedYou: number;
  evolvedBot: number;
  claimedYou: number; // craters you captured
  lostYou: number; // craters you lost
  peakPopYou: number; // peak living cells, either side
  peakPopBot: number;
  peakNodesYou: number; // peak craters held, either side
  peakNodesBot: number;
}

// ---- difficulty copy for the post-game screen (matches the intro; Fable-written) ----
const DIFF_LABEL: Record<Difficulty, string> = {
  tidepool: 'Tidepool',
  reef: 'Reef',
  abyss: 'Abyss',
  constrictor: 'Constrictor',
};

// The full running-game shell. Nothing here runs until the player picks a water + Descends
// (solo) or a lockstep match starts (PvP). `sim` is built by the caller — createSim({difficulty})
// for solo, createSim({seed}) for PvP — and `pvp` is present only in a networked match.
function runMatch(sim: ReturnType<typeof createSim>, difficulty: Difficulty, pvp?: PvpCtx): void {
  const pvpMode = !!pvp;

  // ---- mode-aware command dispatch --------------------------------------
  // In SOLO, UI intent hits the sim directly (sim.command). In PVP, every command is a lockstep
  // INPUT: it must reach the peer and execute on BOTH sims at the same (delayed) tick, so it goes
  // through session.submitCommand INSTEAD of sim.command. Rather than rewire ui/input.ts,
  // ui/hud.ts, ui/evolve.ts (which each call `.command` on the sim handed to them), we hand those
  // mounts a thin wrapper whose ONLY difference is `.command`. Every read/pick method and the live
  // `state` reference pass straight through the spread, so render/hud/evolve are byte-identical.
  // ---- match RECORDER (solo only) --------------------------------------
  // The sim is bit-deterministic (mulberry32 seeded off state.seed; the bot re-runs inside update()
  // as a pure fn of that stream), so we can rewatch the whole match from just { seed, difficulty } +
  // the human command log. We stamp each command with the current tick (advanced-update count) and
  // replay by re-simulating. Pause commands are skipped (they don't advance ticks and the replay
  // never pauses). See ui/replay.ts. PvP isn't recorded here (the lockstep already owns both streams).
  const recording: ReplayRecording | null = pvpMode
    ? null
    : { seed: sim.state.seed, difficulty, endTick: 0, cmds: [] };
  let recTick = 0;
  const recordCmd = (cmd: Command): void => {
    if (!recording || cmd.t === 'pause') return;
    recording.cmds.push({ t: recTick, c: JSON.parse(JSON.stringify(cmd)) as Command });
  };

  const gameSim: ReturnType<typeof createSim> = pvpMode
    ? { ...sim, command: (cmd: Command) => pvp!.session.submitCommand(cmd) }
    : { ...sim, command: (cmd: Command) => { recordCmd(cmd); sim.command(cmd); } };

  // The LOCAL player's side — solo is always 'you'; PvP is the session's assigned side. It drives
  // ONLY presentation (which colony's selection/groups this client sees + commands): it never
  // enters the sim or the determinism hash. Threaded into render + the command-source mounts so a
  // PvP peer on side 'bot' selects/among/sees its OWN units, not the 'you' colony's.
  const viewSide: Side = pvpMode ? pvp!.side : 'you';

  const { render, cam, minimapHit, destroy: destroyRender } = mountRender(canvas!, sim, { viewSide });
  const floatingJoystick = createFloatingJoystick(cam);
  const gameInput = attachGameInput(canvas!, gameSim, cam, haptic, floatingJoystick, viewSide);
  const miniInput = attachMinimapInput(minimapHit, gameSim, cam, haptic, viewSide);
  const hud = mountHud(hudRoot!, gameSim, viewSide);
  const evolve = mountEvolve(evolveRoot!, gameSim, viewSide);

  // Let the module-scoped tutorial pause/resume THIS match while its help sheet is open. In PVP a
  // local pause would desync the lockstep, so the help sheet opens WITHOUT pausing (no-op here) and
  // the match rides on underneath.
  gamePause = pvpMode ? () => {} : (on) => sim.command({ t: 'pause', on });
  gameIsOver = () => sim.state.result.over;
  gameIsPaused = () => sim.state.clocks.paused;

  // The in-game "?" help now lives in the HUD's bottom control bar; it fires 'microbe:help',
  // wired once at module scope to open the (pausing) tutorial. (see the listener below the mount)

  const stats: MatchStats = {
    producedYou: 0, evolvedYou: 0, evolvedBot: 0, claimedYou: 0, lostYou: 0,
    peakPopYou: 0, peakPopBot: 0, peakNodesYou: 0, peakNodesBot: 0,
  };

  // One-shot prompt the first time the player unlocks turrets (the "Sessile Fortress" mutation).
  // Without this nothing tells them HOW to place one — the Turret chip lives on the Home dock.
  let turretsPrompted = sim.turretsUnlocked(viewSide);

  // Only YOUR events (or globally-relevant ones) toast; the audio blips are player-side only.
  // This is also where cumulative match stats are tallied off the event stream.
  function drainEvents(): void {
    // Surface the fortress placement hint the instant it unlocks (fires off the 'evolved' event that
    // the buy pushes, so events is non-empty this frame and we don't early-return past it).
    if (!turretsPrompted && sim.turretsUnlocked(viewSide)) {
      turretsPrompted = true;
      showToast('🗼 Sessile Fortress ready. Tap your Home, then the Turret chip, to grow one.', 5600);
    }
    const events = sim.state.events;
    if (events.length === 0) return;
    for (const e of events) {
      // audio blips (player-side)
      if (e.side === 'you') {
        if (e.kind === 'produced') audio.play('produce');
        else if (e.kind === 'nodeClaimed') audio.play('claim');
        else if (e.kind === 'evolved') audio.play('evolve');
        else if (e.kind === 'nodeLost') audio.play('lost');
        else if (e.kind === 'underAttack') audio.play('attack');
      }
      // tally
      if (e.kind === 'produced' && e.side === 'you') stats.producedYou++;
      else if (e.kind === 'evolved') (e.side === 'you' ? stats.evolvedYou++ : stats.evolvedBot++);
      else if (e.kind === 'nodeClaimed' && e.side === 'you') stats.claimedYou++;
      else if (e.kind === 'nodeLost' && e.side === 'you') stats.lostYou++;
      const line = EVENT_TEXT[e.kind]?.(e) ?? null;
      if (line) showToast(line);
    }
    // UI owns draining this transient queue (see types.ts).
    events.length = 0;
  }

  // Sample live peaks each UI tick (living cells + craters held, both sides).
  function sampleStats(): void {
    let popYou = 0, popBot = 0;
    for (const u of sim.state.units) {
      if (u.hp <= 0) continue;
      if (u.side === 'you') popYou++;
      else if (u.side === 'bot') popBot++;
    }
    let nodesYou = 0, nodesBot = 0;
    for (const n of sim.state.nodes) {
      if (n.owner === 'you') nodesYou++;
      else if (n.owner === 'bot') nodesBot++;
    }
    if (popYou > stats.peakPopYou) stats.peakPopYou = popYou;
    if (popBot > stats.peakPopBot) stats.peakPopBot = popBot;
    if (nodesYou > stats.peakNodesYou) stats.peakNodesYou = nodesYou;
    if (nodesBot > stats.peakNodesBot) stats.peakNodesBot = nodesBot;
  }

  // ---- opening objective: no home is owned yet — you drop a squad and must go colonize one ----
  if (!sim.nodeById(sim.state.colonies.you.homeId)) {
    showToast('Lead your squad to a home crater and colonize it', 5200);
  }

  // ---- post-game analysis overlay ----
  let overlayShown = false;
  function checkResult(): void {
    const r = sim.state.result;
    if (!r.over || overlayShown) return;
    overlayShown = true;
    sampleStats(); // final sample so end-state peaks are captured
    if (r.winner === 'you') audio.play('evolve');
    else audio.play('lost');
    haptic(r.winner === 'you' ? [12, 40, 12] : [20, 40]);
    // Solo only: "Watch replay" re-simulates the whole match from the recorded command log in a
    // no-fog god view with playback controls. It owns the screen, so we first tear the live match
    // view down (stop the loop, destroy the render + inputs, hide the dock) to avoid double-drawing.
    let liveTornDown = false;
    const onReview = pvpMode || !recording
      ? undefined
      : (showSummary: () => void) => {
          const rec = recording!; // non-null in this branch (guarded above)
          rec.endTick = recTick; // total ticks the match ran → the timeline length
          if (!liveTornDown) {
            liveTornDown = true;
            loop.stop();
            gameInput.destroy?.();
            miniInput.destroy?.();
            hud.destroy?.(); // stop the 5 Hz dock repaint + drop its listeners
            evolve.destroy?.();
            destroyRender();
            hudRoot!.style.display = 'none';
            evolveRoot!.style.display = 'none';
          }
          startReplay(canvas!, rec, showSummary);
        };
    document.body.appendChild(buildPostGame(sim, stats, difficulty, r.winner, r.reason, pvpMode, onReview));
  }

  // ---- the fixed-step loop ----
  // hud/evolve self-refresh on their own timers, but the design asks the shell to refresh them
  // each frame. Rebuilding the HUD's dock at 60fps thrashes the DOM on a phone, so throttle the
  // UI repaint to ~10/s here — the visual result is identical, the battery is happier.
  let lastUi = 0;
  const UI_INTERVAL_MS = 100;

  const loop = createLoop({
    step: 1 / 30,
    // SOLO: advance the sim on the fixed step. PVP: the lockstep session owns advancement —
    // session.step() gates on both players' input frames for the next tick, applies them in
    // canonical order (you-cmds then bot-cmds), ticks the sim, and runs the checksum canary. When
    // the peer's frame hasn't arrived it no-ops (natural lockstep stall) and retries next frame.
    update: pvpMode
      ? () => { pvp!.session.step(); }
      : (dt) => {
          // Count only ticks that actually advance the sim (mirrors update()'s own gate), so a
          // recorded command's tick lines up 1:1 with a replay step (which never pauses).
          const advanced = !sim.state.clocks.paused && !sim.state.result.over && dt > 0;
          sim.update(dt);
          if (advanced) recTick++;
        },
    render: (alpha) => {
      render(alpha);
      const now = performance.now();
      if (now - lastUi >= UI_INTERVAL_MS) {
        lastUi = now;
        hud.refresh?.();
        evolve.refresh?.();
        sampleStats();
      }
      drainEvents();
      checkResult();
    },
  });

  // ---- full-freeze when backgrounded (per design: the sim pauses, not just the loop) ----
  // Keep it paused if the help sheet holds a pause, so returning from background doesn't unpause the
  // match out from under an open tutorial (the two pause owners share one boolean).
  document.addEventListener('visibilitychange', () => {
    // A networked lockstep match can't unilaterally pause without desyncing the peer — ride through
    // backgrounding (if it drops the socket, session.onEnd/peer_gone ends the match cleanly).
    if (pvpMode) return;
    sim.command({ t: 'pause', on: document.hidden || pausedByHelp });
  });

  // ---- PvP network end (peer left / desync / engine issue) ----
  // The normal win/lose path is checkResult() → Fossil Record card. onEnd covers the network-level
  // terminations the sim can't see; whichever fires first wins the single `overlayShown` latch.
  if (pvpMode) {
    pvp!.session.onEnd((reason) => {
      if (overlayShown) return;
      overlayShown = true;
      loop.stop();
      audio.play('lost');
      haptic([20, 40]);
      document.body.appendChild(buildPvpEnd(reason));
    });
  }

  loop.start();
}

// ---- post-game analysis card (Fable-written headers/subs) ----
const WIN_SUB: Record<string, string> = {
  conquest: 'Their queen dissolves. The deep is yours.',
  domination: 'Every crater beats with your blood.',
  oxygenation: 'The poison tide rose. You alone endured.',
};
const LOSE_SUB: Record<string, string> = {
  conquest: 'Your queen is devoured. The colony scatters.',
  domination: 'The map turned against you, crater by crater.',
  oxygenation: 'The oxygen came. Your rival outlasted you.',
};

function buildPostGame(
  sim: ReturnType<typeof createSim>,
  stats: MatchStats,
  difficulty: Difficulty,
  winner: Side | null,
  reason: string,
  isPvp = false,
  onReview?: (onExit: () => void) => void,
): HTMLElement {
  const won = winner === 'you';
  const st = sim.state;

  // final end-state figures
  const alive = (side: Side): number => st.units.filter((u) => u.side === side && u.hp > 0).length;
  const held = (side: Side): number => st.nodes.filter((n) => n.owner === side).length;
  const finalPopYou = alive('you'), finalPopBot = alive('bot');
  const finalNodesYou = held('you'), finalNodesBot = held('bot');
  const t = Math.floor(st.clocks.sim);
  const clock = `${String((t / 60) | 0).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;

  const wrap = document.createElement('div');
  wrap.className = 'result-overlay';
  const card = document.createElement('div');
  card.className = `result-card ${won ? 'win' : 'lose'}`;

  const h = document.createElement('h1');
  h.textContent = won ? 'Apex Strain' : winner === 'bot' ? 'Lineage Extinct' : 'Stalemate';
  const sub = document.createElement('p');
  sub.className = 'result-sub';
  sub.textContent = (won ? WIN_SUB : LOSE_SUB)[reason] ?? '';

  const meta = document.createElement('div');
  meta.className = 'result-meta';
  meta.textContent = `${isPvp ? 'Versus' : DIFF_LABEL[difficulty]} · ${clock}`;

  // section title (Fable)
  const secTitle = document.createElement('div');
  secTitle.className = 'stats-title';
  secTitle.textContent = 'The Fossil Record';

  // head-to-head comparison table (You vs Rival)
  const grid = document.createElement('div');
  grid.className = 'stats-grid';
  const cmp: [string, number | string, number | string][] = [
    ['Craters held', finalNodesYou, finalNodesBot],
    ['Peak craters', stats.peakNodesYou, stats.peakNodesBot],
    ['Cells alive', finalPopYou, finalPopBot],
    ['Peak swarm', stats.peakPopYou, stats.peakPopBot],
    ['Genome tier', `T${st.colonies.you.tier}`, `T${st.colonies.bot.tier}`],
    ['Mutations', stats.evolvedYou, stats.evolvedBot],
  ];
  const headHtml =
    `<div class="stats-row stats-head"><span class="stats-label"></span>` +
    `<span class="stats-you">You</span><span class="stats-bot">Rival</span></div>`;
  // Numeric-aware lead compare so string rows like 'T3' vs 'T2' still highlight the leader.
  const num = (v: number | string): number =>
    typeof v === 'number' ? v : parseFloat(String(v).replace(/[^0-9.]/g, ''));
  const rowsHtml = cmp
    .map(([label, y, b]) => {
      const yn = num(y), bn = num(b);
      const yWin = Number.isFinite(yn) && Number.isFinite(bn) && yn > bn;
      const bWin = Number.isFinite(yn) && Number.isFinite(bn) && bn > yn;
      return (
        `<div class="stats-row"><span class="stats-label">${label}</span>` +
        `<span class="stats-you${yWin ? ' lead' : ''}">${y}</span>` +
        `<span class="stats-bot${bWin ? ' lead' : ''}">${b}</span></div>`
      );
    })
    .join('');
  grid.innerHTML = headHtml + rowsHtml;

  // solo highlight chips
  const chips = document.createElement('div');
  chips.className = 'stats-chips';
  const chip = (label: string, val: number | string): string =>
    `<span class="stats-chip"><b>${val}</b>${label}</span>`;
  chips.innerHTML =
    chip('cells bred', stats.producedYou) +
    chip('craters claimed', stats.claimedYou) +
    chip('craters lost', stats.lostYou) +
    chip('rival assaults', st.clocks.botWave);

  // actions
  const actions = document.createElement('div');
  actions.className = 'result-actions';
  if (isPvp) {
    // A PvP rematch needs a fresh code exchange, so there's no one-tap replay — back to the lobby.
    const lobby = document.createElement('button');
    lobby.type = 'button';
    lobby.className = 'result-rematch';
    lobby.textContent = 'Back to lobby';
    lobby.addEventListener('click', () => {
      haptic(10);
      try { sessionStorage.removeItem(AUTOSTART_KEY); } catch { /* ignore */ }
      location.reload();
    });
    actions.append(lobby);
  } else {
    const again = document.createElement('button');
    again.type = 'button';
    again.className = 'result-rematch';
    again.textContent = 'Rematch';
    again.addEventListener('click', () => {
      haptic(12);
      try { sessionStorage.setItem(AUTOSTART_KEY, difficulty); } catch { /* ignore */ }
      location.reload();
    });
    const menu = document.createElement('button');
    menu.type = 'button';
    menu.className = 'result-menu';
    menu.textContent = 'Change water';
    menu.addEventListener('click', () => {
      haptic(8);
      try { sessionStorage.removeItem(AUTOSTART_KEY); } catch { /* ignore */ }
      location.reload();
    });
    actions.append(again, menu);
  }

  // ---- "Watch replay": re-simulate the whole match in a no-fog god view with playback controls.
  // Hides this card while the replay owns the screen; the replay's Exit button brings it back.
  if (onReview) {
    const review = document.createElement('button');
    review.type = 'button';
    review.className = 'result-menu';
    review.textContent = '▶ Watch replay';
    review.addEventListener('click', () => {
      haptic(8);
      wrap.style.display = 'none';
      onReview(() => { wrap.style.display = ''; });
    });
    actions.append(review);
  }

  card.append(h, sub, meta, secTitle, grid, chips, actions);
  wrap.append(card);
  return wrap;
}

// ---- PvP network-end overlay (peer left / desync / a plain winner reason) ----
// Shown from session.onEnd for the terminations the sim itself can't see. Single "Back to lobby"
// action — a PvP rematch needs a fresh code, so there is no one-tap replay.
function buildPvpEnd(reason: string): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'result-overlay';
  const card = document.createElement('div');
  card.className = 'result-card pvp-end-card';

  const h = document.createElement('h1');
  const p = document.createElement('p');
  const r = (reason || '').toLowerCase();
  if (r.includes('peer') || r.includes('gone') || r.includes('left')) {
    h.textContent = 'Peer left';
    p.textContent = 'Your rival colony went dark. The match is over.';
  } else if (r.includes('desync')) {
    h.textContent = 'Desynced';
    p.textContent = 'The two colonies drifted out of step. Rematch to try again.';
  } else if (r.includes('engine')) {
    h.textContent = 'Engine mismatch';
    p.textContent = 'That match needs both players on the same browser engine (Safari/iOS).';
  } else if (r.includes('error')) {
    h.textContent = 'Connection lost';
    p.textContent = 'The link to your rival dropped. The match is over.';
  } else if (r === 'you' || r.includes('win')) {
    h.textContent = 'Apex Strain';
    p.textContent = 'The deep is yours.';
  } else if (r === 'bot' || r.includes('los')) {
    h.textContent = 'Lineage Extinct';
    p.textContent = 'Your rival outlasted you.';
  } else {
    h.textContent = 'Match ended';
    p.textContent = reason || 'The match is over.';
  }

  const actions = document.createElement('div');
  actions.className = 'result-actions';
  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'result-rematch';
  back.textContent = 'Back to lobby';
  back.addEventListener('click', () => {
    haptic(10);
    try { sessionStorage.removeItem(AUTOSTART_KEY); } catch { /* ignore */ }
    location.reload();
  });
  actions.append(back);

  card.append(h, p, actions);
  wrap.append(card);
  return wrap;
}

// ---- How-to-Play tutorial ----
// Mounted once. In-game it PAUSES the match while open (and only resumes what it paused). These hooks
// are wired by startGame so the module-scope tutorial can reach the running match.
let gamePause: ((on: boolean) => void) | null = null;
let gameIsOver: (() => boolean) | null = null;
let gameIsPaused: (() => boolean) | null = null;
let pausedByHelp = false;
const SEEN_TUTORIAL_KEY = 'mrts-seen-tutorial';
const tutorialRoot = document.getElementById('tutorial');
const tutorial = tutorialRoot
  ? mountTutorial(tutorialRoot, haptic, {
      onOpen: () => {
        // pause a live, not-yet-decided match that the player hasn't already paused
        if (gamePause && gameIsOver && gameIsPaused && !gameIsOver() && !gameIsPaused()) {
          pausedByHelp = true;
          gamePause(true);
        }
      },
      onClose: () => {
        try { localStorage.setItem(SEEN_TUTORIAL_KEY, '1'); } catch { /* ignore */ }
        if (pausedByHelp && gamePause) {
          gamePause(false);
          pausedByHelp = false;
        }
      },
    })
  : null;

// The HUD control-bar "?" button (built in ui/hud.ts during a live match) fires 'microbe:help';
// open the how-to-play sheet, which pauses the match while it's up (onOpen guard above).
window.addEventListener('microbe:help', () => tutorial?.open());

// ---- landing → intro / difficulty gate ----
const AUTOSTART_KEY = 'mrts-autostart-diff';
const launchRoot = document.getElementById('launch');
const introRoot = document.getElementById('intro');

function launch(difficulty: Difficulty): void {
  if (launchRoot) launchRoot.hidden = true;
  if (introRoot) introRoot.hidden = true;
  runMatch(createSim({ difficulty }), difficulty);
}

// PvP entry — the transport has fired onStart(seed, side). Human vs human: build the sim from the
// SHARED seed (no bothBots — neither side is AI-driven), create the lockstep session over the SAME
// transport, and run the match in PvP mode. (Takes the transport, not a session, because the
// session must be created with the sim we build here — createPvpSession(sim, transport).)
function startPvp(seed: number, side: Side, transport: Transport): void {
  if (launchRoot) launchRoot.hidden = true;
  if (introRoot) introRoot.hidden = true;
  const panel = document.getElementById('launch-pvp');
  if (panel) panel.hidden = true;
  // PvP: NO bot AI — both colonies are human-driven through the lockstep. (Solo passes no pvp flag,
  // so its 'bot' colony keeps its macro-AI rival.)
  const sim = createSim({ seed, pvp: true });
  const session = createPvpSession(sim, transport);
  runMatch(sim, 'reef', { session, side });
}

// A short, unambiguous match code (no I/L/O/0/1) minted from crypto — the creator shows it, the
// joiner types it. The DO keys the room off this; side is assigned by join order.
function makeMatchCode(): string {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += alphabet[bytes[i] % alphabet.length];
  return out;
}

// Wire the (BETA) PvP lobby: reveal the panel, toggle Create/Join sub-views, open the transport,
// and surface engine warnings. Back closes any pending socket and returns to the mode picker.
function wirePvpLobby(): void {
  const panel = document.getElementById('launch-pvp');
  const modes = launchRoot?.querySelector<HTMLElement>('.launch-modes') ?? null;
  const chooseView = document.getElementById('pvp-choose');
  const createView = document.getElementById('pvp-create');
  const joinView = document.getElementById('pvp-join');
  const engineNote = document.getElementById('pvp-engine-note');
  const codeEl = document.getElementById('pvp-code');
  const createStatus = document.getElementById('pvp-create-status');
  const joinStatus = document.getElementById('pvp-join-status');
  const codeInput = document.getElementById('pvp-code-input') as HTMLInputElement | null;

  // The pending pre-match socket. Once a match starts it's handed to the session and this is nulled,
  // so a later Back never tears down a live game.
  let transport: Transport | null = null;

  function showView(which: 'choose' | 'create' | 'join'): void {
    if (chooseView) chooseView.hidden = which !== 'choose';
    if (createView) createView.hidden = which !== 'create';
    if (joinView) joinView.hidden = which !== 'join';
  }
  function setStatus(el: HTMLElement | null, msg: string, isError: boolean): void {
    if (!el) return;
    el.hidden = false;
    el.textContent = msg;
    el.classList.toggle('is-error', isError);
  }
  function resetLobby(): void {
    transport?.close();
    transport = null;
    if (codeInput) codeInput.value = '';
    if (joinStatus) { joinStatus.hidden = true; joinStatus.textContent = ''; joinStatus.classList.remove('is-error'); }
    setStatus(createStatus, 'Waiting for your friend to join…', false);
    showView('choose');
  }

  // Open one WebSocket for `code` and wire the shared-contract callbacks. `statusEl` shows errors.
  // `settled` latches once we've either started or shown a terminal message, so a trailing
  // socket-close (WsTransport reports every close as an error) can't clobber the clearer note.
  function begin(code: string, statusEl: HTMLElement | null): void {
    transport?.close();
    const t = connectMatch(code, { engine: detectEngine() });
    transport = t;
    let settled = false;
    t.onStart((seed, side) => {
      settled = true;
      transport = null; // ownership passes to the running match
      startPvp(seed, side, t);
    });
    t.onEngineMismatch(() => {
      settled = true;
      setStatus(statusEl, 'Your friend is on a different browser engine. Both need Safari/iOS.', true);
    });
    t.onError(() => {
      if (settled) return;
      setStatus(statusEl, 'Connection lost. Check the code and try again.', true);
    });
  }

  document.getElementById('launch-versus')?.addEventListener('click', () => {
    haptic(8);
    if (modes) modes.hidden = true;
    if (panel) panel.hidden = false;
    // Non-blocking engine nudge: WebKit ('jsc') is the golden path; warn on anything else.
    if (engineNote) engineNote.hidden = detectEngine() === 'jsc';
    resetLobby();
  });
  document.getElementById('launch-pvp-back')?.addEventListener('click', () => {
    haptic(6);
    resetLobby();
    if (panel) panel.hidden = true;
    if (modes) modes.hidden = false;
  });
  document.getElementById('pvp-create-btn')?.addEventListener('click', () => {
    haptic(8);
    const code = makeMatchCode();
    if (codeEl) codeEl.textContent = code;
    setStatus(createStatus, 'Waiting for your friend to join…', false);
    showView('create');
    begin(code, createStatus); // creator = first joiner = side 'you'
  });
  document.getElementById('pvp-join-btn')?.addEventListener('click', () => {
    haptic(8);
    if (joinStatus) { joinStatus.hidden = true; joinStatus.classList.remove('is-error'); }
    showView('join');
    codeInput?.focus();
  });
  function submitJoin(): void {
    const code = (codeInput?.value || '').trim().toUpperCase();
    if (code.length < 4) { setStatus(joinStatus, 'Enter the 4-character code from your friend.', true); return; }
    haptic(10);
    setStatus(joinStatus, 'Connecting…', false);
    begin(code, joinStatus); // joiner = second joiner = side 'bot'
  }
  document.getElementById('pvp-connect-btn')?.addEventListener('click', submitJoin);
  codeInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); submitJoin(); }
  });
}

// Wire the EXISTING difficulty gate (Tidepool/Reef/Abyss/Constrictor → Descend). Safe to attach
// while the gate is still hidden behind the landing; nothing here reveals or starts anything.
function wireDiffGate(): void {
  if (!introRoot) return;
  let chosen: Difficulty = 'reef';
  const diffBtns = Array.from(introRoot.querySelectorAll<HTMLButtonElement>('.diff'));
  for (const btn of diffBtns) {
    btn.addEventListener('click', () => {
      const d = btn.dataset.diff as Difficulty;
      if (d !== 'tidepool' && d !== 'reef' && d !== 'abyss' && d !== 'constrictor') return;
      chosen = d;
      haptic(6);
      for (const b of diffBtns) {
        const on = b === btn;
        b.classList.toggle('is-sel', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      }
    });
  }
  const begin = document.getElementById('intro-begin');
  begin?.addEventListener('click', () => {
    haptic(14);
    launch(chosen);
  });
  // "How to play" opens the guide over the gate.
  document.getElementById('intro-howto')?.addEventListener('click', () => tutorial?.open());
}

// Practice Offline → reveal the difficulty gate exactly as before (this is the whole single-player
// entry). First-ever visit still auto-opens the guide, now on top of the gate.
function showDiffGate(): void {
  if (launchRoot) launchRoot.hidden = true;
  if (introRoot) introRoot.hidden = false;
  let seen = false;
  try { seen = localStorage.getItem(SEEN_TUTORIAL_KEY) === '1'; } catch { /* ignore */ }
  if (!seen && tutorial) tutorial.open();
}

// Rematch path: the post-game "Rematch" reloads with the difficulty stashed — skip the landing AND
// the gate, drop straight into a fresh match.
let autostart: string | null = null;
try { autostart = sessionStorage.getItem(AUTOSTART_KEY); sessionStorage.removeItem(AUTOSTART_KEY); } catch { /* ignore */ }
if (autostart === 'tidepool' || autostart === 'reef' || autostart === 'abyss' || autostart === 'constrictor') {
  launch(autostart);
} else if (launchRoot && introRoot) {
  wireDiffGate();
  // Landing: Practice Offline advances to the difficulty gate.
  document.getElementById('launch-solo')?.addEventListener('click', () => {
    haptic(10);
    showDiffGate();
  });
  // Landing: Play Others opens the real (BETA) PvP lobby — Create/Join over input-lockstep netcode.
  wirePvpLobby();
  // iOS-Safari-on-web only: a one-time dismissible nudge to install to the Home Screen (self-gates).
  maybeShowA2HS();
} else if (introRoot) {
  // No landing element (shouldn't happen) — fall back to the old direct-gate flow.
  wireDiffGate();
  showDiffGate();
} else {
  // No intro element either — last-ditch straight boot.
  launch('reef');
}
